import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { CheckIcon, XCircle, ChevronDown, WandSparkles } from 'lucide-react'

import { cn } from '@/common/utils/utils'
import useIsMobile from '@/common/hooks/useIsMobile'
import { Separator } from '@/common/ui/separator'
import { Button } from '@/common/ui/button'
import { Badge } from '@/common/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Sheet, SheetContent, SheetFooter, SheetTrigger } from '@/common/ui/sheet'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/common/ui/command'
import { CopyCheck } from 'lucide-react'

const multiSelectVariants = cva('m-1 transition ease-in-out delay-150 duration-300', {
    variants: {
        variant: {
            default: 'border-foreground/10 text-foreground bg-card hover:bg-lime-50',
            secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
            destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
            inverted: 'inverted',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
})

interface MultiSelectProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof multiSelectVariants> {
    title?: string
    options: {
        label: string
        value: string
        description?: string
        icon?: React.ComponentType<{ className?: string }>
    }[]
    onValueChange: (value: string[]) => void
    onSelectAll?: () => void
    onCloseSelection?: (selectedValues: string[]) => void
    value?: string[]
    defaultValue?: string[]
    placeholder?: string
    label?: string
    animation?: number
    maxCount?: number
    modalPopover?: boolean
    className?: string
    closeLabel?: string
    clearLabel?: string
}

type MultiSelectOption = MultiSelectProps['options'][number]

interface MultiSelectTriggerProps {
    label: string
    placeholder: string
    selectedValues: string[]
    options: MultiSelectOption[]
    maxCount: number
    variant?: VariantProps<typeof multiSelectVariants>['variant']
    animation: number
    isAnimating: boolean
    className?: string
    onRemoveValue: (value: string) => void
    onClearExtra: () => void
}

const MultiSelectTriggerButton = React.forwardRef<
    HTMLButtonElement,
    MultiSelectTriggerProps & React.ButtonHTMLAttributes<HTMLButtonElement>
>(
    (
        {
            label,
            placeholder,
            selectedValues,
            options,
            maxCount,
            variant,
            animation,
            isAnimating,
            className,
            onRemoveValue,
            onClearExtra,
            ...props
        },
        ref,
    ) => (
        <Button
            ref={ref}
            {...props}
            className={cn(
                'flex w-full p-1 border-content-primary border-1 rounded-full min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto',
                className,
            )}
            innerClassName="flex-1"
            midClassName="flex-1"
        >
            <div className="text-foreground pl-2">{label}</div>
            {selectedValues.length > 0 ? (
                <div className="flex justify-between items-center w-full flex-1">
                    <div className="flex items-center flex-1">
                        {selectedValues.slice(0, maxCount).map((value) => {
                            const option = options.find((o) => o.value === value)
                            const IconComponent = option?.icon

                            return (
                                <Badge
                                    key={value}
                                    className={cn(
                                        '!bg-primary-dust max-w-[100px] md:max-w-[120px] lg:max-w-[140px] font-normal',
                                        isAnimating ? 'animate-bounce' : '',
                                        multiSelectVariants({ variant }),
                                    )}
                                    style={{ animationDuration: `${animation}s` }}
                                    title={option?.label}
                                >
                                    {IconComponent && <IconComponent className="h-4 w-4 mr-2" />}
                                    <span className="truncate">{option?.label}</span>
                                    <XCircle
                                        className="ml-2 h-4 w-4 cursor-pointer"
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            onRemoveValue(value)
                                        }}
                                    />
                                </Badge>
                            )
                        })}

                        {selectedValues.length > maxCount && (
                            <Badge
                                className={cn(
                                    '!bg-primary-dust text-foreground border-foreground/1 hover:bg-transparent font-normal',
                                    isAnimating ? 'animate-bounce' : '',
                                    multiSelectVariants({ variant }),
                                )}
                                style={{ animationDuration: `${animation}s` }}
                            >
                                {`+ ${selectedValues.length - maxCount}`}
                                <XCircle
                                    className="ml-2 h-4 w-4 cursor-pointer"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        onClearExtra()
                                    }}
                                />
                            </Badge>
                        )}
                    </div>

                    <div className="flex items-center justify-between">
                        <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between w-full mx-auto">
                    <span className="text-sm text-muted-foreground mx-3">{placeholder}</span>
                    <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
                </div>
            )}
        </Button>
    ),
)
MultiSelectTriggerButton.displayName = 'MultiSelectTriggerButton'

interface MultiSelectCommandContentProps {
    options: MultiSelectOption[]
    isMobile: boolean
    selectedValues: string[]
    onToggleAll: () => void
    onToggleOption: (value: string) => void
    onClear: () => void
    onClose: () => void
    onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
    closeLabel?: string
    clearLabel?: string
}

const MultiSelectCommandContent = ({
    options,
    isMobile,
    selectedValues,
    onToggleAll,
    onToggleOption,
    onClear,
    onClose,
    onInputKeyDown,
    closeLabel,
    clearLabel,
}: MultiSelectCommandContentProps) => (
    <Command>
        {!isMobile && <CommandInput placeholder="Search..." onKeyDown={onInputKeyDown} />}
        <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
                <CommandItem
                    key="all"
                    onSelect={onToggleAll}
                    className="cursor-pointer h-[46px] sm:h-auto flex flex-row-reverse md:flex-row"
                >
                    <div
                        className={cn(
                            'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                            selectedValues.length === options.length
                                ? 'bg-primary text-primary-foreground'
                                : 'opacity-50 [&_svg]:invisible',
                        )}
                    >
                        <CheckIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 flex items-start">
                        <CopyCheck className="!text-brand-primary-forest mr-2 h-4 w-4 md:hidden text-muted-foreground" />
                        Select All
                    </div>
                </CommandItem>
                {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value)

                    return (
                        <div key={option.value}>
                            <div className="md:hidden self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
                            <CommandItem
                                key={option.value}
                                onSelect={() => onToggleOption(option.value)}
                                className="cursor-pointer min-h-[46px] sm:h-auto flex flex-row-reverse md:flex-row justify-start items-start gap-2 py-3"
                            >
                                <div
                                    className={cn(
                                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                                        isSelected
                                            ? 'bg-primary text-primary-foreground'
                                            : 'opacity-50 [&_svg]:invisible',
                                    )}
                                >
                                    <CheckIcon className="h-4 w-4" />
                                </div>
                                <div className="flex-1 flex flex-col items-start gap-2">
                                    <div className="flex items-start gap-2">
                                        {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                                        <span>{option.label}</span>
                                    </div>
                                    {option.description && (
                                        <div className="pl-6 sm:pl-0 self-stretch justify-start text-content-secondary text-xs font-normal font-['Inter'] leading-4">
                                            {option.description}
                                        </div>
                                    )}
                                </div>
                            </CommandItem>
                        </div>
                    )
                })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
                <div className="hidden sm:flex items-center justify-between">
                    {selectedValues.length > 0 && (
                        <>
                            <CommandItem onSelect={onClear} className="flex-1 justify-center cursor-pointer">
                                {clearLabel}
                            </CommandItem>
                            <Separator orientation="vertical" className="flex min-h-6 h-full" />
                        </>
                    )}
                    <CommandItem onSelect={onClose} className="flex-1 justify-center cursor-pointer max-w-full">
                        {closeLabel}
                    </CommandItem>
                </div>
            </CommandGroup>
        </CommandList>
    </Command>
)

const ControlButtons = ({
    disableClear,
    onClear,
    onClose,
    closeLabel,
    clearLabel,
}: {
    disableClear: boolean
    onClear: () => void
    onClose: () => void
    closeLabel?: string
    clearLabel?: string
}) => (
    <div className="w-full px-4 py-3 bg-white shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] border-t border-border-outline inline-flex justify-start items-start gap-2.5 overflow-hidden">
        <Button
            variant="ghost"
            onClick={onClear}
            className="px-6 border-none disabled:bg-white"
            disabled={disableClear}
        >
            {clearLabel}
        </Button>
        <Button variant="secondary" onClick={onClose} className="flex-1">
            {closeLabel}
        </Button>
    </div>
)

export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
    (
        {
            title,
            options = [],
            onValueChange,
            onSelectAll,
            variant,
            value,
            defaultValue = [],
            placeholder = 'Select filters',
            label = 'Filters',
            animation = 0,
            maxCount = 3,
            modalPopover = false,
            className,
            closeLabel = 'Close',
            clearLabel = 'Clear',
            onCloseSelection,
            ...props
        },
        ref,
    ) => {
        const isMobile = useIsMobile()
        const [selectedValues, setSelectedValues] = React.useState<string[]>(value ?? defaultValue)
        const [isOpen, setIsOpen] = React.useState(false)
        const [isAnimating, setIsAnimating] = React.useState(false)

        React.useEffect(() => {
            if (value) {
                setSelectedValues(value)
            }
        }, [value])

        const prevIsOpenRef = React.useRef(false)
        React.useEffect(() => {
            if (prevIsOpenRef.current && !isOpen) {
                onCloseSelection?.(selectedValues)
            }
            prevIsOpenRef.current = isOpen
        }, [isOpen, selectedValues, onCloseSelection])

        const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                setIsOpen(true)
            } else if (event.key === 'Backspace' && !event.currentTarget.value) {
                const newSelectedValues = [...selectedValues]
                newSelectedValues.pop()
                setSelectedValues(newSelectedValues)
                onValueChange(newSelectedValues)
            }
        }

        const toggleOption = (option: string) => {
            const newSelectedValues = selectedValues.includes(option)
                ? selectedValues.filter((value) => value !== option)
                : [...selectedValues, option]
            setSelectedValues(newSelectedValues)
            onValueChange(newSelectedValues)
        }

        const handleClear = () => {
            setSelectedValues([])
            onValueChange([])
        }

        const clearExtraOptions = () => {
            const newSelectedValues = selectedValues.slice(0, maxCount)
            setSelectedValues(newSelectedValues)
            onValueChange(newSelectedValues)
        }

        const toggleAll = () => {
            if (selectedValues.length === options.length) {
                handleClear()
            } else {
                const allValues = options.map((option) => option.value)
                setSelectedValues(allValues)
                onValueChange(allValues)
            }
            onSelectAll?.()
        }

        const triggerButton = (
            <MultiSelectTriggerButton
                ref={ref}
                {...props}
                label={label}
                placeholder={placeholder}
                selectedValues={selectedValues}
                options={options}
                maxCount={maxCount}
                variant={variant}
                animation={animation}
                isAnimating={isAnimating}
                className={className}
                onRemoveValue={toggleOption}
                onClearExtra={clearExtraOptions}
            />
        )

        const commandContent = (
            <MultiSelectCommandContent
                options={options}
                isMobile={isMobile}
                selectedValues={selectedValues}
                onToggleAll={toggleAll}
                onToggleOption={toggleOption}
                onClear={handleClear}
                onClose={() => setIsOpen(false)}
                onInputKeyDown={handleInputKeyDown}
                closeLabel={closeLabel}
                clearLabel={clearLabel}
            />
        )

        const sparkleControl =
            animation > 0 && selectedValues.length > 0 ? (
                <WandSparkles
                    className={cn(
                        'cursor-pointer my-2 text-foreground bg-background w-3 h-3',
                        isAnimating ? '' : 'text-muted-foreground',
                    )}
                    onClick={() => setIsAnimating(!isAnimating)}
                />
            ) : null

        if (isMobile) {
            return (
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                    <SheetTrigger asChild>{triggerButton}</SheetTrigger>
                    <SheetContent
                        side="bottom"
                        className="w-full !max-h-[95vh] p-0 pt-12 px-2"
                        onClose={() => setIsOpen(false)}
                    >
                        {title && (
                            <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5 px-2">
                                {title}
                            </div>
                        )}
                        <div className="flex-1 min-h-0 overflow-y-auto">{commandContent}</div>
                        <SheetFooter>
                            <ControlButtons
                                disableClear={selectedValues.length === 0}
                                onClear={handleClear}
                                onClose={() => setIsOpen(false)}
                                closeLabel={closeLabel}
                                clearLabel={clearLabel}
                            />
                        </SheetFooter>
                    </SheetContent>
                    {sparkleControl}
                </Sheet>
            )
        }

        return (
            <Popover open={isOpen} onOpenChange={setIsOpen} modal={modalPopover}>
                <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
                <PopoverContent className="max-w-96 p-0" align="start" onEscapeKeyDown={() => setIsOpen(false)}>
                    {commandContent}
                </PopoverContent>
                {sparkleControl}
            </Popover>
        )
    },
)
